import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { EditorSettingName } from '@/msgdeskRuntime/contracts/editorModel'
import { useEditorSession } from '../../EditorSessionContext'
import { useEditorSurface } from '../../EditorSurfaceContext'
import { delegatedSettingChange, useSettingSelectGroups } from './SettingSelectState'

// Counts fieldset — legacy "Qty:" toggle + six per-level combos. Option values,
// labels, AND their DOM order are the legacy new-form contract 1:1
// (searchpg2012 reference DOM, 2026-07-17): slots 0/1 list value 2 ("0 - hide")
// first. is_view_count_1 exists only on msg desks (m31 i===27).
const countCombos: Array<{
  name: EditorSettingName
  description: string
  msgOnly?: boolean
  options: Array<[number, string]>
}> = [
  {
    name: 'is_view_count_0',
    description: 'Answers on main page',
    options: [
      [2, '0 - hide the answers on the main page'],
      [1, '4 answers on the main page'],
      [3, '8 answers on the main page'],
      [4, '12 answers on the main page'],
      [5, '16 answers on the main page'],
    ],
  },
  {
    name: 'is_view_count_1',
    description: 'Messages on main page',
    msgOnly: true,
    options: [
      [2, '0 - hide msges on main page'],
      [1, '4 msges on the main page'],
      [3, '8 msges on the main page'],
      [4, '12 msges on the main page'],
      [5, '16 msges on the main page'],
    ],
  },
  {
    name: 'is_view_count_2',
    description: 'Items per tab page',
    options: [
      [1, '20 answers / msges on tabs'],
      [2, '32 answers / msges on tabs'],
      [3, '44 answers / msges on tabs'],
      [4, '60 answers / msges on tabs'],
      [5, '72 answers / msges on tabs'],
    ],
  },
  {
    name: 'is_view_count_3',
    description: 'New-reply hierarchy depth',
    options: [
      [1, '--- unlimited depth'],
      [2, '1 - one parent'],
      [3, '8 sub answers / msges no more'],
      [4, '12 sub answers / msges no more'],
      [5, '16 sub answers / msges no more'],
    ],
  },
  {
    name: 'is_view_count_4',
    description: 'Tree opened on first load',
    options: [
      [1, '0 - Do not show answers / msges'],
      [6, '1 nested level / 2 levels total'],
      [2, '4 sub answers / msges show no more'],
      [3, '8 sub answers / msges show no more'],
      [4, '12 sub answers / msges show no more'],
      [5, '16 sub answers / msges show no more'],
    ],
  },
  {
    name: 'is_view_count_5',
    description: 'Visible parent trail',
    options: [
      [1, '--- show all parents'],
      [2, '1 show no more than parents'],
      [3, '2 show no more than parents'],
      [4, '3 show no more than parents'],
      [5, '4 show no more than parents'],
    ],
  },
]

const countSelectGroups = [{
  master: 'is_view_count',
  combos: countCombos.map(combo => combo.name),
}] as const

export function CountsPanel() {
  const { skin } = useEditorSurface()
  const classes = skin.settings
  const { isEnabled, selectValue } = useSettingSelectGroups(countSelectGroups)
  // §0.16: message-only combos come from the shared editor-session identity
  // (fail-closed), never from the independently synchronized rough model.
  const editorSession = useEditorSession()
  const isMsg = editorSession.isMsg === true
  const countsEnabled = isEnabled('is_view_count')
  return (
    <fieldset {...legacyContractDataAttrs('editor.settings.counts')} className={classes.panel}>
      <legend className={classes.legend}>Counts</legend>
      <label className={classes.check}>
        <input type="checkbox" name="is_view_count" value="1" checked={countsEnabled} onChange={delegatedSettingChange} />
        Qty:
      </label>
      {countCombos.map(combo => {
        const hidden = combo.msgOnly ? !isMsg : false
        return (
          <label
            key={combo.name}
            className={classes.row}
            hidden={hidden}
            style={hidden ? { display: 'none' } : undefined}
          >
            <span className={classes.rowCheck}>{combo.description}</span>
            <select name={combo.name} className={classes.combo} disabled={!countsEnabled} value={selectValue(combo.name)} onChange={delegatedSettingChange}>
              {combo.options.map(([value, text]) => <option key={value} value={String(value)}>{text}</option>)}
            </select>
          </label>
        )
      })}
    </fieldset>
  )
}
